import SEO from "@/components/SEO";
import Breadcrumb from "@/components/Breadcrumb";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import InstantQuoteCTA from "@/components/InstantQuoteCTA";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Phone, CheckCircle2, Clock, Shield, Navigation, Car, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import ServiceFAQ from "@/components/ServiceFAQ";
import { generateLocalBusinessSchema } from "@/lib/schemaGenerator";
import satAirportImg from "@/assets/sat-airport-night.jpg";
const AirportTransportation = () => {
  // SAT Airport Car Service FAQs - Optimized for high-intent keywords
  const faqs = [
    {
      question: "How much does San Antonio Airport Car Service cost?",
      answer: "Our San Antonio Airport Car Service offers transparent, flat-rate pricing starting at just $85 for transfers to and from San Antonio International Airport (SAT). Unlike ride-share apps, we never charge surge pricing—the quote you receive is the final price, with no hidden fees. Contact our 24/7 Black Car Service at (210) 296-7409 for an instant quote."
    },
    {
      question: "Is your San Antonio Airport Car Service available 24/7?",
      answer: "Yes! Our 24/7 Black Car Service operates around the clock, 365 days a year. Whether you need a 4 AM pickup for an early flight or a midnight arrival at San Antonio International Airport (SAT), our professional chauffeurs are always available. We specialize in stress-free airport transfers at any hour."
    },
    {
      question: "Do you offer Flight Tracking for SAT airport pickups?",
      answer: "Absolutely. Our San Antonio Airport Car Service includes complimentary Flight Tracking for every pickup at San Antonio International Airport (SAT). We monitor your flight in real-time and automatically adjust your pickup time for delays or early arrivals—at no extra charge. Your chauffeur will be waiting exactly when you land."
    },
    {
      question: "What is included with your Meet & Greet service at SAT?",
      answer: "Every San Antonio Airport Car Service booking includes a professional Meet & Greet at San Antonio International Airport (SAT). Your chauffeur will text you upon arrival with the vehicle description and license plate, then meet you curbside at baggage claim. Combined with our Flight Tracking technology, you'll experience seamless, stress-free airport transportation."
    },
    {
      question: "How far in advance should I book your 24/7 Black Car Service?",
      answer: "We recommend booking your San Antonio Airport Car Service at least 24 hours in advance for guaranteed availability. However, our 24/7 Black Car Service often accommodates same-day requests—just call (210) 296-7409. With transparent, flat-rate pricing and Flight Tracking included, booking early ensures the best experience to or from San Antonio International Airport (SAT)."
    }
  ];

  // FAQPage Schema for rich snippets
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://ricksblackcarservicesantonio.com/airport-transportation#service",
    "name": "San Antonio Airport Car Service",
    "alternateName": "SAT Airport Transportation",
    "serviceType": "Airport Transportation",
    "provider": {
      "@type": "LocalBusiness",
      "@id": "https://ricksblackcarservicesantonio.com/#business",
      "name": "Rick's Black Car Service",
      "telephone": "+1-210-296-7409",
      "openingHours": "Mo-Su 00:00-23:59"
    },
    "areaServed": {
      "@type": "City",
      "name": "San Antonio",
      "containedInPlace": {
        "@type": "State",
        "name": "Texas"
      }
    },
    "description": "Premium 24/7 San Antonio airport car service to SAT and AUS airports. Professional chauffeur service with flight tracking, meet & greet, and luxury Audi Q7. Starting at $85.",
    "image": [
      "https://ricksblackcarservicesantonio.com/og-image.jpg"
    ],
    "offers": {
      "@type": "AggregateOffer",
      "url": "https://ricksblackcarservicesantonio.com/airport-transportation",
      "priceCurrency": "USD",
      "lowPrice": "115",
      "highPrice": "395",
      "offerCount": "2",
      "priceValidUntil": "2026-12-31",
      "availability": "https://schema.org/InStock"
    },
    "priceRange": "$115-$395",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Airport Transportation Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "name": "SAT Airport Transfer",
          "description": "Professional car service to/from San Antonio International Airport with flight tracking and meet & greet",
          "price": "115",
          "priceCurrency": "USD",
          "priceValidUntil": "2026-12-31",
          "availability": "https://schema.org/InStock",
          "itemOffered": {
            "@type": "Service",
            "name": "SAT Airport Transfer"
          }
        },
        {
          "@type": "Offer",
          "name": "AUS Airport Transfer",
          "description": "Luxury transportation to Austin-Bergstrom International Airport from San Antonio",
          "price": "395",
          "priceCurrency": "USD",
          "priceValidUntil": "2026-12-31",
          "availability": "https://schema.org/InStock",
          "itemOffered": {
            "@type": "Service",
            "name": "AUS Airport Transfer"
          }
        }
      ]
    }
  };

  const schemaMarkup = [faqSchema, serviceSchema];
  return <>
      <SEO 
        title="Premium SAT Airport Car Service | Rick's Black Car Service"
        description="Premium SAT airport car service. Enjoy a stress-free meet-and-greet and a smooth ride in our 2025 Audi Q7. Flat-rate local transfers."
        keywords="san antonio airport car service, sat airport transfers, black car service to sat airport, san antonio airport shuttle, airport transfers"
        canonicalPath="/airport-transportation"
        schema={schemaMarkup}
      />

      <div className="min-h-screen">
        <Navbar />
        <main className="pt-20">
          <Breadcrumb 
            items={[
              { name: "Services", url: "/services" },
              { name: "SAT Airport Transportation", url: "/airport-transportation" }
            ]}
          />

          {/* Hero Section */}
          <section className="relative h-[60vh] overflow-hidden flex items-center justify-center">
            <div className="absolute inset-0 z-0">
              <img src={satAirportImg} alt="San Antonio SAT Airport transportation - professional chauffeur service" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-b from-background/30 via-background/40 to-background/50" />
            </div>
            <div className="container mx-auto px-4 relative z-10 text-center">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-bold text-foreground mb-6">
                Premium SAT Airport Transfers &amp; Black Car Service San Antonio
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto">
                Professional SAT Airport Transfers | Starting at $85 | Available 24/7
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button className="btn-luxury text-lg px-8 py-6" asChild>
                  <a href="tel:+12102967409">
                    <Phone className="w-5 h-5 mr-2" />
                    Call Now: 210-296-7409
                  </a>
                </Button>
                <Button className="btn-luxury text-lg px-8 py-6" asChild>
                  <a href="http://ricksblackcarservicesantonio.square.site" target="_blank" rel="noopener noreferrer">
                    Book Your Private Executive Car Service Today
                  </a>
                </Button>
              </div>
            </div>
          </section>

          {/* Main Content */}
          <section className="py-20 luxury-gradient">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <div className="prose prose-lg max-w-none">
                  <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                    Looking for reliable <strong className="text-foreground">Black Car Service to SAT Airport</strong>? Rick's Black Car Service provides premium airport transportation to and from San Antonio International Airport (SAT) and Austin-Bergstrom International Airport (AUS). Our professional chauffeur service ensures you arrive on time, every time, in the comfort of our luxurious 2025 Audi Q7 Black Optic.
                  </p>
                  
                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4 mt-8">
                    Stress-Free Private Car Services to San Antonio International Airport (SAT)
                  </h2>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                    Our <strong className="text-foreground">SAT Airport Transfers</strong> offer competitive, transparent pricing for airport transportation throughout San Antonio. Airport transfers start at just $85, with no hidden fees or surge pricing. Experience the difference of professional black car service to SAT Airport.
                  </p>
                  
                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-6 mt-8">
                    Why Choose Rick's for Airport Transportation
                  </h2>
                </div>

                {/* CTA Card */}
                <Card className="card-luxury my-12 border-2 border-primary/20">
                  <CardContent className="p-10 text-center">
                    <h2 className="text-3xl font-serif font-bold text-foreground mb-4">
                      Book Your Private Executive Car Service Today
                    </h2>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
                      <Button className="btn-luxury text-lg px-8 py-6 w-full sm:w-auto" asChild>
                        <a href="tel:+12102967409">
                          📞 Call Now: 210-296-7409
                        </a>
                      </Button>
                      <Button className="btn-luxury text-lg px-8 py-6 w-full sm:w-auto" asChild>
                        <a href="http://ricksblackcarservicesantonio.square.site" target="_blank" rel="noopener noreferrer">
                          🚗 Book Online - Instant Quote
                        </a>
                      </Button>
                    </div>
                    <p className="text-muted-foreground text-lg">
                      Available 24/7 | Professional Chauffeurs | Luxury Fleet
                    </p>
                  </CardContent>
                </Card>

                {/* Service Features */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 my-12">
                  <Card className="card-luxury">
                    <CardContent className="p-8">
                      <div className="flex items-start gap-4">
                        <Clock className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                        <div>
                          <h3 className="text-xl font-semibold text-foreground mb-3">Flight Tracking Technology</h3>
                          <p className="text-muted-foreground leading-relaxed">
                            We monitor your flight in real-time, automatically adjusting pickup times for delays or early arrivals.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="card-luxury">
                    <CardContent className="p-8">
                      <div className="flex items-start gap-4">
                        <Shield className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                        <div>
                          <h3 className="text-xl font-semibold text-foreground mb-3">Professional Meet and Greet Service</h3>
                          <p className="text-muted-foreground leading-relaxed">For arriving passengers, we provide complimentary meet and greet service outside baggage claim with a description of vehicle with license plate.</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="card-luxury">
                    <CardContent className="p-8">
                      <div className="flex items-start gap-4">
                        <CheckCircle2 className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                        <div>
                          <h3 className="text-xl font-semibold text-foreground mb-3">Stress-Free Travel</h3>
                          <p className="text-muted-foreground leading-relaxed">
                            Skip the hassle of parking fees, ride-share surge pricing, and unreliable transportation.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="card-luxury">
                    <CardContent className="p-8">
                      <div className="flex items-start gap-4">
                        <Car className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                        <div>
                          <h3 className="text-xl font-semibold text-foreground mb-3">Spacious Luggage Capacity</h3>
                          <p className="text-muted-foreground leading-relaxed">
                            Our Audi Q7 comfortably accommodates up to 4 passengers with luggage for SAT airport transfers.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="card-luxury">
                    <CardContent className="p-8">
                      <div className="flex items-start gap-4">
                        <Clock className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                        <div>
                          <h3 className="text-xl font-semibold text-foreground mb-3">24/7 Availability</h3>
                          <p className="text-muted-foreground leading-relaxed">
                            Early morning flights? Late-night arrivals? We're available around the clock, 365 days a year.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="card-luxury">
                    <CardContent className="p-8">
                      <div className="flex items-start gap-4">
                        <Shield className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                        <div>
                          <h3 className="text-xl font-semibold text-foreground mb-3">Professional Chauffeur Service</h3>
                          <p className="text-muted-foreground leading-relaxed">
                            With over 5 years of professional experience, your chauffeur understands the importance of punctuality and safe driving.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Perfect For Section */}
                <div className="prose prose-lg max-w-none my-12">
                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-6">
                    Perfect For
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-xl font-semibold text-primary mb-2">Business Travelers</h3>
                      <p className="text-muted-foreground text-lg leading-relaxed">
                        Impress clients, prepare for meetings, or catch up on emails during your ride. Corporate accounts available.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-primary mb-2">Leisure Travelers</h3>
                      <p className="text-muted-foreground text-lg leading-relaxed">
                        Start your vacation stress-free with reliable SAT airport transportation.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-primary mb-2">Group Travel</h3>
                      <p className="text-muted-foreground text-lg leading-relaxed">
                        Traveling with family or colleagues? Our Audi Q7 accommodates up to 4 passengers with luggage.
                      </p>
                    </div>
                  </div>
                  
                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-6 mt-12">
                    Service to Multiple Airports
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-xl font-semibold text-primary mb-2">San Antonio International Airport (SAT)</h3>
                      <p className="text-muted-foreground text-lg leading-relaxed">
                        Primary service airport with curbside pickup and drop-off.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-primary mb-2">Austin-Bergstrom International Airport (AUS)</h3>
                      <p className="text-muted-foreground text-lg leading-relaxed">
                        Comfortable, direct transportation to AUS, approximately 75 miles from San Antonio.
                      </p>
                    </div>
                  </div>
                  
                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-6 mt-12">
                    How to Book
                  </h2>
                  <div className="space-y-3 text-muted-foreground text-lg">
                    <p><strong className="text-foreground">Call or Text:</strong> (210) 296-7409 (available 24/7)</p>
                    <p><strong className="text-foreground">Email:</strong> ricksblackcarservice@outlook.com</p>
                    <p><strong className="text-foreground">Online:</strong> Use our convenient booking form</p>
                  </div>
                </div>

                {/* Service Areas */}
                <Card className="card-luxury my-12">
                  <CardContent className="p-10">
                    <h3 className="text-2xl font-serif font-bold text-foreground mb-4">
                      Service Areas
                    </h3>
                    <p className="text-muted-foreground text-lg mb-6">
                      We provide SAT airport transportation throughout San Antonio including Downtown, Alamo Heights, Stone Oak, The Dominion, Medical Center, Terrell Hills, Boerne, New Braunfels, Schertz, and all surrounding communities.
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                        <span>Downtown San Antonio</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                        <span>Alamo Heights</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                        <span>Stone Oak</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                        <span>The Dominion</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                        <span>Medical Center</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                        <span>Terrell Hills</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                        <span>Boerne</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                        <span>New Braunfels</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                        <span>Schertz</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* FAQ Section */}
                <ServiceFAQ faqs={faqs} />

                {/* Final CTA */}
                <div className="text-center my-12">
                  <h2 className="text-3xl font-serif font-bold text-foreground mb-6">
                    Don't Leave Your SAT Airport Transportation to Chance
                  </h2>
                  <p className="text-muted-foreground text-lg mb-8">
                    Reserve your professional black car service today. Call (210) 296-7409. SAT airport transfers starting at $85. Available 24/7.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <Button className="btn-luxury text-xl px-10 py-7" asChild>
                      <a href="tel:+12102967409">
                        <Phone className="w-6 h-6 mr-2" />
                        Call: 210-296-7409
                      </a>
                    </Button>
                    <Button className="btn-luxury text-xl px-10 py-7" asChild>
                      <Link to="/book">
                        Book Your Ride Now <ArrowRight className="w-6 h-6 ml-2" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Instant Quote CTA */}
          <InstantQuoteCTA />
        </main>
        <Footer />
      </div>
    </>;
};
export default AirportTransportation;